import React, { useCallback, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { NextButton } from 'components/Common/MUIStyledComponents';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import { toBase64, downloadBase64Data } from 'utils/fileHelper';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface IPDFUploadProps {
  setFieldValue: any;
  documents: any;
  existingCase: boolean;
}

const PDFUpload: React.FC<IPDFUploadProps> = (props: IPDFUploadProps) => {
  const { setFieldValue, documents, existingCase } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [size, setSize] = useState(0);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(async (accptFile) => {
      const reader = new FileReader();
      reader.onabort = () => {
        enqueueSnackbar('File reading aborted', { variant: 'info' });
      };
      reader.onerror = () => {
        enqueueSnackbar('Error reading file', { variant: 'error' });
      };
      setName(accptFile.name);
      setSize(accptFile.size);
      handleFileUpload(accptFile, 0);
    });
  }, []);

  const fileValidator = (file: any) => {
    if (file.size > 40000000) {
      enqueueSnackbar('Your file is too large, upload a file less than 40mbs', {
        variant: 'error',
      });
      return {
        code: 'size-too-large',
        message: 'file is larger than 40MB',
      };
    }
    return null;
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: '.pdf',
  });

  const handleFileUpload = async (file: any, index: any) => {
    const base64Str = await toBase64(file);
    const new_val = {
      uploadedFile: {
        base64: base64Str,
        name: file.name,
      },
    };
    setFieldValue(`documents.${index}.uploadedFile`, new_val.uploadedFile);
  };

  const handleFileRemove = (index: any) => {
    const empty_val = {
      uploadedFile: {
        base64: '',
        name: '',
      },
    };
    setFieldValue(`documents.${index}.uploadedFile`, empty_val.uploadedFile);
    setName('');
    setSize(0);
  };

  return (
    <>
      {existingCase && documents[0] ? (
        <>
          <div className='vault-pdf-container-existing'>
            <div className='vault-doc-header'>
              <p>Results:</p>
              <h6>{documents && documents[0]?.uploadedFile.name}</h6>
            </div>
            <Document
              file={documents && documents[0]?.uploadedFile.base64}
              className='pdf-document'
              error={
                <div className='vault-doc-error'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='32'
                    height='32'
                    viewBox='0 0 32 32'
                    fill='none'
                  >
                    <path
                      d='M21.3333 26.668H10.6667C9.6058 26.668 8.58839 26.2465 7.83824 25.4964C7.08809 24.7463 6.66667 23.7288 6.66667 22.668V9.33464C6.66667 8.98101 6.52619 8.64188 6.27614 8.39183C6.02609 8.14178 5.68696 8.0013 5.33333 8.0013C4.97971 8.0013 4.64057 8.14178 4.39052 8.39183C4.14048 8.64188 4 8.98101 4 9.33464V22.668C4 24.4361 4.70238 26.1318 5.95262 27.382C7.20286 28.6323 8.89856 29.3346 10.6667 29.3346H21.3333C21.687 29.3346 22.0261 29.1942 22.2761 28.9441C22.5262 28.6941 22.6667 28.3549 22.6667 28.0013C22.6667 27.6477 22.5262 27.3085 22.2761 27.0585C22.0261 26.8084 21.687 26.668 21.3333 26.668ZM13.3333 17.3346C13.3333 17.6883 13.4738 18.0274 13.7239 18.2774C13.9739 18.5275 14.313 18.668 14.6667 18.668H21.3333C21.687 18.668 22.0261 18.5275 22.2761 18.2774C22.5262 18.0274 22.6667 17.6883 22.6667 17.3346C22.6667 16.981 22.5262 16.6419 22.2761 16.3918C22.0261 16.1418 21.687 16.0013 21.3333 16.0013H14.6667C14.313 16.0013 13.9739 16.1418 13.7239 16.3918C13.4738 16.6419 13.3333 16.981 13.3333 17.3346ZM28 11.9213C27.9861 11.7988 27.9593 11.6781 27.92 11.5613V11.4413C27.8559 11.3042 27.7704 11.1782 27.6667 11.068L19.6667 3.06797C19.5564 2.96426 19.4304 2.87874 19.2933 2.81464C19.2535 2.80898 19.2131 2.80898 19.1733 2.81464C19.0379 2.73696 18.8883 2.68709 18.7333 2.66797H13.3333C12.2725 2.66797 11.2551 3.0894 10.5049 3.83954C9.75476 4.58969 9.33333 5.6071 9.33333 6.66797V20.0013C9.33333 21.0622 9.75476 22.0796 10.5049 22.8297C11.2551 23.5799 12.2725 24.0013 13.3333 24.0013H24C25.0609 24.0013 26.0783 23.5799 26.8284 22.8297C27.5786 22.0796 28 21.0622 28 20.0013V12.0013C28 12.0013 28 12.0013 28 11.9213ZM20 7.21464L23.4533 10.668H21.3333C20.9797 10.668 20.6406 10.5275 20.3905 10.2774C20.1405 10.0274 20 9.68826 20 9.33464V7.21464ZM25.3333 20.0013C25.3333 20.3549 25.1929 20.6941 24.9428 20.9441C24.6928 21.1942 24.3536 21.3346 24 21.3346H13.3333C12.9797 21.3346 12.6406 21.1942 12.3905 20.9441C12.1405 20.6941 12 20.3549 12 20.0013V6.66797C12 6.31435 12.1405 5.97521 12.3905 5.72516C12.6406 5.47511 12.9797 5.33464 13.3333 5.33464H17.3333V9.33464C17.3369 9.78949 17.4181 10.2404 17.5733 10.668H14.6667C14.313 10.668 13.9739 10.8084 13.7239 11.0585C13.4738 11.3085 13.3333 11.6477 13.3333 12.0013C13.3333 12.3549 13.4738 12.6941 13.7239 12.9441C13.9739 13.1942 14.313 13.3346 14.6667 13.3346H25.3333V20.0013Z'
                      fill='white'
                    />
                  </svg>
                  <h2>Preview Not Available</h2>
                </div>
              }
            >
              <Page height={395} pageNumber={1} width={260} />
            </Document>
          </div>
          <div
            className='vault-pdf-download-btn'
            onClick={() =>
              downloadBase64Data(
                documents[0]?.uploadedFile.base64,
                documents[0]?.uploadedFile.name,
              )
            }
          >
            <h2>Download</h2>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M15 6.705C14.9922 6.6361 14.9771 6.56822 14.955 6.5025V6.435C14.9189 6.35788 14.8708 6.287 14.8125 6.225L10.3125 1.725C10.2505 1.66666 10.1796 1.61856 10.1025 1.5825H10.0275C9.95457 1.5435 9.8762 1.51569 9.795 1.5H5.25C4.65326 1.5 4.08097 1.73705 3.65901 2.15901C3.23705 2.58097 3 3.15326 3 3.75V14.25C3 14.8467 3.23705 15.419 3.65901 15.841C4.08097 16.2629 4.65326 16.5 5.25 16.5H12.75C13.3467 16.5 13.919 16.2629 14.341 15.841C14.7629 15.419 15 14.8467 15 14.25V6.75C15 6.75 15 6.75 15 6.705ZM10.5 4.0575L12.4425 6H11.25C11.0511 6 10.8603 5.92098 10.7197 5.78033C10.579 5.63968 10.5 5.44891 10.5 5.25V4.0575ZM13.5 14.25C13.5 14.4489 13.421 14.6397 13.2803 14.7803C13.1397 14.921 12.9489 15 12.75 15H5.25C5.05109 15 4.86032 14.921 4.71967 14.7803C4.57902 14.6397 4.5 14.4489 4.5 14.25V3.75C4.5 3.55109 4.57902 3.36032 4.71967 3.21967C4.86032 3.07902 5.05109 3 5.25 3H9V5.25C9 5.84674 9.23705 6.41903 9.65901 6.84099C10.081 7.26295 10.6533 7.5 11.25 7.5H13.5V14.25ZM9.9675 10.7175L9.75 10.9425V9C9.75 8.80109 9.67098 8.61032 9.53033 8.46967C9.38968 8.32902 9.19891 8.25 9 8.25C8.80109 8.25 8.61032 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V10.9425L8.0325 10.7175C7.89127 10.5763 7.69973 10.4969 7.5 10.4969C7.30027 10.4969 7.10873 10.5763 6.9675 10.7175C6.82627 10.8587 6.74693 11.0503 6.74693 11.25C6.74693 11.4497 6.82627 11.6413 6.9675 11.7825L8.4675 13.2825C8.53883 13.3508 8.62294 13.4043 8.715 13.44C8.80478 13.4797 8.90185 13.5002 9 13.5002C9.09815 13.5002 9.19522 13.4797 9.285 13.44C9.37706 13.4043 9.46117 13.3508 9.5325 13.2825L11.0325 11.7825C11.1737 11.6413 11.2531 11.4497 11.2531 11.25C11.2531 11.0503 11.1737 10.8587 11.0325 10.7175C10.8913 10.5763 10.6997 10.4969 10.5 10.4969C10.3003 10.4969 10.1087 10.5763 9.9675 10.7175Z'
                fill='var(--alternative-text-color)'
              />
            </svg>
          </div>
        </>
      ) : (
        <div className='vault-pdf-container'>
          <p>PDF Upload</p>
          <div {...getRootProps()}>
            <div
              className={
                !name
                  ? 'vault-files-container-tall'
                  : 'vault-files-container-short'
              }
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='32'
                height='32'
                viewBox='0 0 32 32'
                fill='none'
              >
                <path
                  d='M16.9468 15.0527C16.82 14.9313 16.6705 14.8361 16.5068 14.7727C16.1822 14.6393 15.8181 14.6393 15.4935 14.7727C15.3298 14.8361 15.1803 14.9313 15.0535 15.0527L12.3868 17.7193C12.1358 17.9704 11.9947 18.3109 11.9947 18.666C11.9947 19.0211 12.1358 19.3616 12.3868 19.6127C12.6379 19.8638 12.9784 20.0048 13.3335 20.0048C13.6886 20.0048 14.0291 19.8638 14.2802 19.6127L14.6668 19.2127V22.666C14.6668 23.0196 14.8073 23.3588 15.0574 23.6088C15.3074 23.8589 15.6465 23.9993 16.0002 23.9993C16.3538 23.9993 16.6929 23.8589 16.943 23.6088C17.193 23.3588 17.3335 23.0196 17.3335 22.666V19.2127L17.7202 19.6127C17.8441 19.7377 17.9916 19.8368 18.1541 19.9045C18.3165 19.9722 18.4908 20.0071 18.6668 20.0071C18.8428 20.0071 19.0171 19.9722 19.1796 19.9045C19.3421 19.8368 19.4895 19.7377 19.6135 19.6127C19.7385 19.4887 19.8377 19.3413 19.9053 19.1788C19.973 19.0163 20.0079 18.842 20.0079 18.666C20.0079 18.49 19.973 18.3157 19.9053 18.1532C19.8377 17.9908 19.7385 17.8433 19.6135 17.7193L16.9468 15.0527ZM26.6668 11.9193C26.6529 11.7969 26.6261 11.6762 26.5868 11.5593V11.4393C26.5227 11.3023 26.4372 11.1762 26.3335 11.066L18.3335 3.06602C18.2233 2.9623 18.0973 2.87679 17.9602 2.81268H17.8268C17.6972 2.74335 17.5578 2.69391 17.4135 2.66602H9.3335C8.27263 2.66602 7.25521 3.08744 6.50507 3.83759C5.75492 4.58773 5.3335 5.60515 5.3335 6.66602V25.3327C5.3335 26.3935 5.75492 27.411 6.50507 28.1611C7.25521 28.9113 8.27263 29.3327 9.3335 29.3327H22.6668C23.7277 29.3327 24.7451 28.9113 25.4953 28.1611C26.2454 27.411 26.6668 26.3935 26.6668 25.3327V11.9993C26.6668 11.9993 26.6668 11.9993 26.6668 11.9193ZM18.6668 7.21268L22.1202 10.666H20.0002C19.6465 10.666 19.3074 10.5255 19.0574 10.2755C18.8073 10.0254 18.6668 9.6863 18.6668 9.33268V7.21268ZM24.0002 25.3327C24.0002 25.6863 23.8597 26.0254 23.6096 26.2755C23.3596 26.5255 23.0205 26.666 22.6668 26.666H9.3335C8.97987 26.666 8.64074 26.5255 8.39069 26.2755C8.14064 26.0254 8.00016 25.6863 8.00016 25.3327V6.66602C8.00016 6.31239 8.14064 5.97326 8.39069 5.72321C8.64074 5.47316 8.97987 5.33268 9.3335 5.33268H16.0002V9.33268C16.0002 10.3935 16.4216 11.411 17.1717 12.1611C17.9219 12.9113 18.9393 13.3327 20.0002 13.3327H24.0002V25.3327Z'
                  fill='var(--alternative-text-color)'
                />
              </svg>
              <p>Select a file or drag and drop here</p>
              <input {...getInputProps()} />
              <NextButton>Select File</NextButton>
            </div>
          </div>
          {name ? (
            <div className='file-list'>
              <p>{name}</p>
              <div className='file-size-div'>
                <p>{Math.round((size / 1000) * 10) / 10}KB</p>
                <div
                  className='cursor-pointer'
                  onClick={() => handleFileRemove(0)}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M7.5 13.5C7.69891 13.5 7.88968 13.421 8.03033 13.2803C8.17098 13.1397 8.25 12.9489 8.25 12.75V8.25C8.25 8.05109 8.17098 7.86032 8.03033 7.71967C7.88968 7.57902 7.69891 7.5 7.5 7.5C7.30109 7.5 7.11032 7.57902 6.96967 7.71967C6.82902 7.86032 6.75 8.05109 6.75 8.25V12.75C6.75 12.9489 6.82902 13.1397 6.96967 13.2803C7.11032 13.421 7.30109 13.5 7.5 13.5ZM15 4.5H12V3.75C12 3.15326 11.7629 2.58097 11.341 2.15901C10.919 1.73705 10.3467 1.5 9.75 1.5H8.25C7.65326 1.5 7.08097 1.73705 6.65901 2.15901C6.23705 2.58097 6 3.15326 6 3.75V4.5H3C2.80109 4.5 2.61032 4.57902 2.46967 4.71967C2.32902 4.86032 2.25 5.05109 2.25 5.25C2.25 5.44891 2.32902 5.63968 2.46967 5.78033C2.61032 5.92098 2.80109 6 3 6H3.75V14.25C3.75 14.8467 3.98705 15.419 4.40901 15.841C4.83097 16.2629 5.40326 16.5 6 16.5H12C12.5967 16.5 13.169 16.2629 13.591 15.841C14.0129 15.419 14.25 14.8467 14.25 14.25V6H15C15.1989 6 15.3897 5.92098 15.5303 5.78033C15.671 5.63968 15.75 5.44891 15.75 5.25C15.75 5.05109 15.671 4.86032 15.5303 4.71967C15.3897 4.57902 15.1989 4.5 15 4.5ZM7.5 3.75C7.5 3.55109 7.57902 3.36032 7.71967 3.21967C7.86032 3.07902 8.05109 3 8.25 3H9.75C9.94891 3 10.1397 3.07902 10.2803 3.21967C10.421 3.36032 10.5 3.55109 10.5 3.75V4.5H7.5V3.75ZM12.75 14.25C12.75 14.4489 12.671 14.6397 12.5303 14.7803C12.3897 14.921 12.1989 15 12 15H6C5.80109 15 5.61032 14.921 5.46967 14.7803C5.32902 14.6397 5.25 14.4489 5.25 14.25V6H12.75V14.25ZM10.5 13.5C10.6989 13.5 10.8897 13.421 11.0303 13.2803C11.171 13.1397 11.25 12.9489 11.25 12.75V8.25C11.25 8.05109 11.171 7.86032 11.0303 7.71967C10.8897 7.57902 10.6989 7.5 10.5 7.5C10.3011 7.5 10.1103 7.57902 9.96967 7.71967C9.82902 7.86032 9.75 8.05109 9.75 8.25V12.75C9.75 12.9489 9.82902 13.1397 9.96967 13.2803C10.1103 13.421 10.3011 13.5 10.5 13.5Z'
                      fill='var(--alternative-text-color)'
                    />
                  </svg>
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      )}
    </>
  );
};

export default PDFUpload;
